import {
  removeParenthesis,
  slugify,
} from '@/lib/data-transformer/gindumacUtils';
import { Filters } from '@/lib/types/api';
import { useTranslationFuz } from '@/lib/types/i18n';
import { getParamValue } from '@/lib/utilities/queryString';
import { useAppDispatch } from '@/redux/hooks';
import useTranslation from 'next-translate/useTranslation';
import Router, { useRouter } from 'next/router';
import { useEffect } from 'react';
import {
  getBrandOption,
  getLocalizationCountryOption,
  getModelOption,
  useGetApplicationTypeOption,
  useGetMachineTypeOption,
} from '../ProductFilters';
import {
  ILocale,
  initialState,
  IOrderby,
  loadFiltersFromUrlParams,
  ProductFilterState,
  ProductFilterStateKeys,
} from './filterSlice';
import { isEmpty } from 'lodash-es';
import { useCurrentCategoryApiSyntax } from '@/lib/hooks/useCurrentCategoryApiSyntax';

const arrayformKeys2SaveInUrl = [
  'brand',
  'model',
  'application_type',
  'machine_type',
  'control_unit_brand',
];
const formKeys2SaveInUrl = [
  ...arrayformKeys2SaveInUrl,
  'page',
  'year_range',
  'localization_country',
  'order_by',
  'search',
  'min_price',
  'max_price',
  'min_xaxis_movement',
  'max_xaxis_movement',
  'min_tablesize',
  'max_tablesize',
  'min_xaxis_sheetmetal',
  'max_xaxis_sheetmetal',
  'min_force_sheetmetal',
  'max_force_sheetmetal',
  'min_thickness_sheetmetal',
  'max_thickness_sheetmetal',
  'min_laser_sheetmetal',
  'max_laser_sheetmetal',
  'min_clamping_force',
  'max_clamping_force',
  'min_screw_diameter',
  'max_screw_diameter',
  'min_maximum_reach',
  'max_maximum_reach',
  'min_maximum_payload',
  'max_maximum_payload',
  'localization_continent',
] as (keyof ProductFilterState)[];

type filtersStorableInUrl = Omit<
  {
    [key in ProductFilterStateKeys | 'locale']: any;
  },
  'order'
>;

export function useFilterUrlParamTranslations() {
  const { t } = useTranslation();
  // those are the keys (product filters) that we want to save on the URL
  const translations: filtersStorableInUrl = {
    brand: t('seo:QUERY_BRAND'),
    application_type: t('seo:QUERY_APPLICATION_TYPES'),
    localization_country: t('products:QUERY_LOCATIONS'),
    machine_type: t('seo:QUERY_MACHINE_TYPES'),
    control_unit_brand: t('products:QUERY_CONTROL_UNIT'),
    year_range: t('products:QUERY_YEAR_RANGE'),
    model: t('products:QUERY_MODELS'),
    min_price: t('products:QUERY_PRICE_FROM'),
    max_price: t('products:QUERY_PRICE_TO'),
    page: t('products:QUERY_PAGE'),
    search: t('products:QUERY_SEARCH_TEXT'),
    order_by: t('products:QUERY_ORDER_BY'),
    machine_category: undefined,
    localization_continent: t('products:QUERY_LOCATION_CONTINENT'),
    locale: t('products:QUERY_LOCATIONS'),
    min_xaxis_movement: t('products:QUERY_MOVEMENT_FROM'),
    max_xaxis_movement: t('products:QUERY_MOVEMENT_TO'),
    min_tablesize: t('products:QUERY_TABLE_SIZE_FROM'),
    max_tablesize: t('products:QUERY_TABLE_SIZE_TO'),
    min_xaxis_sheetmetal: t('products:QUERY_MOVEMENT_FROM'),
    max_xaxis_sheetmetal: t('products:QUERY_MOVEMENT_TO'),
    min_force_sheetmetal: t('products:QUERY_FORCE_FROM'),
    max_force_sheetmetal: t('products:QUERY_FORCE_TO'),
    min_thickness_sheetmetal: t('products:QUERY_THICKNESS_FROM'),
    max_thickness_sheetmetal: t('products:QUERY_THICKNESS_TO'),
    min_laser_sheetmetal: t('products:QUERY_LASER_POWER_FROM'),
    max_laser_sheetmetal: t('products:QUERY_LASER_POWER_TO'),
    min_clamping_force: t('products:QUERY_CLAMPING_FORCE_FROM'),
    max_clamping_force: t('products:QUERY_CLAMPING_FORCE_TO'),
    min_screw_diameter: t('products:QUERY_SCREW_DIAMETER_FROM'),
    max_screw_diameter: t('products:QUERY_SCREW_DIAMETER_TO'),
    min_maximum_reach: t('products:QUERY_MAX_REACH_FROM'),
    max_maximum_reach: t('products:QUERY_MAX_REACH_TO'),
    min_maximum_payload: t('products:QUERY_MAX_PAYLOAD_FROM'),
    max_maximum_payload: t('products:QUERY_MAX_PAYLOAD_TO'),
  };
  return { translations };
}

// Save state in URL
export function useSetFormInUrl(form: ProductFilterState) {
  const { translations } = useFilterUrlParamTranslations();

  function updateUrl() {
    // select which product filters we should store on the URL
    const formParams = {} as any;
    const filtersToSaveInUrl = Object.entries(form).filter(
      ([paramName]) =>
        formKeys2SaveInUrl.includes(paramName as keyof ProductFilterState) &&
        // do not save url params if they are the same as the initial filter state
        initialState?.[paramName as keyof ProductFilterState] !==
          form[paramName as keyof ProductFilterState]
    );
    filtersToSaveInUrl.forEach(([paramName, paramValue]) => {
      console.log('saving to url [paramName, paramValue]', [
        paramName,
        paramValue,
      ]);
      if (!isEmptyValue(paramValue)) {
        const translatedKey =
          translations[paramName as keyof filtersStorableInUrl] ?? paramName;
        console.log('translatedKey', translatedKey);
        if (arrayformKeys2SaveInUrl.includes(paramName)) {
          if (paramName == 'control_unit_brand') {
            formParams[translatedKey] = paramValue.join('_');
          } else {
            formParams[translatedKey] = paramValue
              .map((e: any) => e.slug)
              .join('_');
          }
        } else if (
          paramName === 'localization_country' ||
          paramName === 'localization_continent'
        ) {
          console.log('paramValue', paramValue);
          formParams[translatedKey] = paramValue
            .map((e: any) => e.slug ?? e.toString())
            .join(',');
        } else if (
          ['order_by', 'search', 'page', 'year_range'].includes(paramName)
        ) {
          formParams[translatedKey] = paramValue;
        } else {
          // if it's not an array, just save the value
          formParams[translatedKey] = paramValue.toString();
        }
      }
    });
    console.log('updating url by filters');

    // set a new url with the new filters (shallow is because we don't want to refresh the page)
    // try catch

    try {
      Router.push(
        {
          query: sortObject(formParams),
          pathname: Router.asPath.split('?')[0],
        },
        undefined,
        {
          shallow: true,
        }
      );
    } catch (e) {
      console.log('error', e);
    }
  }

  return { updateUrl };
}
function isEmptyValue(v: any): unknown {
  return v === '' || v === null || (Array.isArray(v) && v.length === 0);
}

// function to sort object by keys alphabetically
function sortObject(o: any) {
  return Object.keys(o)
    .sort()
    .reduce((r: any, k) => ((r[k] = o[k]), r), {});
}

export function useUpdateFiltersByUrl() {
  const dispatch = useAppDispatch();
  const { translations: t } = useFilterUrlParamTranslations();
  const { locale } = useRouter();
  // they are hooks because we need to translate this slug
  const { getAppTypeOption } = useGetApplicationTypeOption();
  const { getMachineTypeOption } = useGetMachineTypeOption();
  const { tFuzz } = useTranslationFuz();
  const categoryApiName = useCurrentCategoryApiSyntax();

  useEffect(() => {
    console.log('useUpdateFiltersByUrl');
    try {
      const serverQueryCache = window.__NEXT_DATA__.props.pageProps
        .dehydratedState.queries as { queryKey: any; state: any }[];
      const getProductsCacheReq = serverQueryCache.find(
        (ck) => ck.queryKey[0] === 'products' && !isEmpty(ck.queryKey?.[1])
      );
      console.log(
        'useUpdateFiltersByUrl getProductsCacheReq',
        getProductsCacheReq
      );
      const getProductsCache = getProductsCacheReq?.state.data.products;
      const filterOptions: Filters = getProductsCache?.data?.filters;
      const result: ProductFilterState & { locale: ILocale } = {
        ...initialState,
        locale: (locale || 'en-US') as ILocale,
      };
      const min_price = getParamValue(t.min_price);
      const max_price = getParamValue(t.max_price);
      const year_range = getParamValue(t.year_range);
      const brand = getParamValue(t.brand);
      const model = getParamValue(t.model);
      const application_type = getParamValue(t.application_type);
      const control_unit_brand = getParamValue(t.control_unit_brand);
      const order_by = getParamValue(t.order_by);
      const machine_type = getParamValue(t.machine_type);
      const localization_country = getParamValue(t.localization_country);
      const localization_continent = getParamValue(t.localization_continent);
      const page = getParamValue(t.page);

      const isMachineToolPage =
        window.__NEXT_DATA__.page.includes('machine-tool');

      const technicalFilters = {
        min_xaxis_movement: isMachineToolPage
          ? getParamValue(t.min_xaxis_movement)
          : undefined,
        max_xaxis_movement: isMachineToolPage
          ? getParamValue(t.max_xaxis_movement)
          : undefined,
        min_tablesize: getParamValue(t.min_tablesize),
        max_tablesize: getParamValue(t.max_tablesize),
        min_xaxis_sheetmetal: !isMachineToolPage
          ? getParamValue(t.min_xaxis_sheetmetal)
          : undefined,
        max_xaxis_sheetmetal: !isMachineToolPage
          ? getParamValue(t.max_xaxis_sheetmetal)
          : undefined,
        min_force_sheetmetal: getParamValue(t.min_force_sheetmetal),
        max_force_sheetmetal: getParamValue(t.max_force_sheetmetal),
        min_thickness_sheetmetal: getParamValue(t.min_thickness_sheetmetal),
        max_thickness_sheetmetal: getParamValue(t.max_thickness_sheetmetal),
        min_laser_sheetmetal: getParamValue(t.min_laser_sheetmetal),
        max_laser_sheetmetal: getParamValue(t.max_laser_sheetmetal),
        min_clamping_force: getParamValue(t.min_clamping_force),
        max_clamping_force: getParamValue(t.max_clamping_force),
        min_screw_diameter: getParamValue(t.min_screw_diameter),
        max_screw_diameter: getParamValue(t.max_screw_diameter),
        min_maximum_reach: getParamValue(t.min_maximum_reach),
        max_maximum_reach: getParamValue(t.max_maximum_reach),
        min_maximum_payload: getParamValue(t.min_maximum_payload),
        max_maximum_payload: getParamValue(t.max_maximum_payload),
      };

      if (page) {
        result.page = parseInt(page.toString());
      }
      if (min_price) {
        result.min_price = parseInt(min_price.toString());
      }
      if (max_price) {
        result.max_price = parseInt(max_price.toString());
      }
      if (year_range) {
        result.year_range = year_range.toString();
      }
      if (brand) {
        const brandSlugs = brand.toString().split('_');
        const selectedBrands =
          filterOptions.brands.filter((b) => {
            return brandSlugs.includes(b.slug);
          }) ?? [];
        const selectedBrandsOptions = selectedBrands.map((b) => {
          return getBrandOption(b);
        });
        result.brand = selectedBrandsOptions;
      }
      if (model) {
        const modelSlugs = model.toString().split('_');
        const selectedmodels =
          filterOptions.models.filter((m) => {
            return modelSlugs.includes(m.slug);
          }) ?? [];
        const selectedModelsOptions = selectedmodels.map((b) => {
          return getModelOption(b);
        });
        result.model = selectedModelsOptions;
      }
      if (application_type) {
        const applicationTypeSlugs = application_type.toString().split('_');
        const selectedTypes =
          filterOptions.application_types.filter((m) => {
            const translatedKey = tFuzz(m.name);
            const slug = slugify(translatedKey);
            return applicationTypeSlugs.includes(slug);
          }) ?? [];
        const selectedAppTypeOptions = selectedTypes.map((appType) =>
          getAppTypeOption(appType)
        );
        result.application_type = selectedAppTypeOptions;
      }
      if (control_unit_brand) {
        const controlUnitBrandSlugs = control_unit_brand.toString().split('_');
        result.control_unit_brand = controlUnitBrandSlugs;
      }
      if (order_by) {
        // TODO: add i18n, at least de-DE is missing, check the rest
        result.order_by = order_by.toString() as IOrderby;
      }
      if (machine_type) {
        const machineTypeSlugs = machine_type.toString().split('_');
        const selectedMachineTypes =
          filterOptions.machine_types.filter((m) => {
            const translatedKey = tFuzz(m.name);
            const slug = removeParenthesis(slugify(translatedKey));
            return machineTypeSlugs.includes(slug);
          }) ?? [];
        const selectedMachineTypeOptions = selectedMachineTypes.map((m) => {
          return getMachineTypeOption(m);
        });
        result.machine_type = selectedMachineTypeOptions;
      }
      if (localization_country) {
        const localizationCountrySlugs = localization_country
          .toString()
          .split('_');
        const selectedLocalizationCountries =
          filterOptions.localization_countries.filter((c) => {
            return localizationCountrySlugs.includes(c.country_code);
          }) ?? [];
        const selectedLocalizationCountryOptions =
          selectedLocalizationCountries.map((m) => {
            return getLocalizationCountryOption(m);
          });
        result.localization_country = selectedLocalizationCountryOptions;
      }
      if (localization_continent) {
        const continents = localization_continent.toString().split('_');
        result.localization_continent = continents;
      }
      for (const [key, value] of Object.entries(technicalFilters)) {
        console.log(`${key}: ${value}`);
        if (value) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          result[key] = parseInt(value);
        }
      }
      console.log('after reset', result);
      dispatch(loadFiltersFromUrlParams(result));
    } catch (error) {
      console.error('filterStateInUrl() error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryApiName]);
}
